import Validator from './validator';
import { ShopError } from '../../internals/errors';
import { modelSort } from '../../utils';
import { forEach } from './../../utils';
import { MetadataType, } from './types';
/**
 *
 *  Used to add the metadata (as BaseMetadataItem) to the forwarded collection.
 *
 *  (i) The default value is null, except for the boolean type to prevent
 *  weird behaviour of checkboxes.
 *
 */
export function addMetadataToCollection(metadata, map, list) {
    let defaultValue = null;
    if (metadata.item.type === MetadataType.Boolean && !metadata.item.extra.includes('required')) {
        defaultValue = false;
    }
    const cartMetaData = {
        item: metadata.item,
        value: defaultValue,
        errors: [],
        index: metadata.index,
    };
    map[metadata.guid] = cartMetaData;
    list.push(cartMetaData);
}
/**
 *
 *  Used to add the metadata of a product to the forwarded collection.
 *
 *  @param product The product to add the metadata from to the collection
 *  @param map The collection to add the product's metadata to
 *  @param list The list to add the product's metadata to
 *  @param appliesTo Only the metadatas that holds this applies_to will be added
 *
 */
export function addMetadataFromProduct(product, map, list, appliesTo) {
    forEach(product.metadata, (metadata) => {
        // Always ignore already added metadata
        if (metadata.item.applies_to !== appliesTo ||
            Object.prototype.hasOwnProperty.call(map, metadata.guid)) {
            return;
        }
        addMetadataToCollection(metadata, map, list);
    });
    list.sort(modelSort);
}
/**
 *  The validator defaults to english, but on language change, the validator should also follow.
 *  Note however, any already generated errors will nog change. Only new generated errors will be in the new language.
 */
export function setValidationLang(locale) {
    // TODO Note: This is a very rudimentary implementation.
    const langMap = {
        en: 'en',
        en_GB: 'en',
        'en-GB': 'en',
        en_US: 'en',
        'en-US': 'en',
        nl: 'nl',
        nl_NL: 'nl',
        'nl-NL': 'nl',
        fr: 'fr',
        fr_FR: 'fr',
        'fr-FR': 'fr',
        de: 'de',
        de_DE: 'de',
        'de-DE': 'de',
        es: 'es',
        es_ES: 'es',
        'es-ES': 'es',
    };
    let lang = 'en';
    if (Object.prototype.hasOwnProperty.call(langMap, locale)) {
        lang = langMap[locale];
    }
    Validator.useLang(lang);
}
/**
 *  Validate metadata will return an error of error messages if its value does not pass the (extra) rules.
 *
 *  By default, when a validation rule fails its error will be added to the errors arrays of the metadata.
 *
 *  @param metadata - The metadata instance to validate
 *  @param mutateErrors - When set to false, any errors will not be written to the metadata errors array
 */
export function validateMetadata(metadata, mutateErrors = true) {
    if (!metadata) {
        throw new ShopError('Failed to validate metadata -> no cart metadata provided');
    }
    // noinspection SuspiciousTypeOfGuard
    if (!metadata.item || !metadata.item.name || typeof metadata.item.name !== 'string') {
        throw new ShopError('Failed to validate metadata -> invalid metadata item');
    }
    // noinspection SuspiciousTypeOfGuard
    if (!Object.prototype.hasOwnProperty.call(metadata.item, 'extra') || !(Array.isArray(metadata.item.extra) || typeof metadata.item.extra === 'string')) {
        throw new ShopError('Failed to validate metadata -> invalid metadata rules');
    }
    // TODO Once the _rules property is ALWAYS set on the metadata.item, the reliance on the extra string can be completely replaced with the _rules property
    if (!Object.prototype.hasOwnProperty.call(metadata, 'value')) {
        throw new ShopError('Failed to validate metadata -> value property not present on cart metadata');
    }
    const name = metadata.item.translateName || metadata.item.name;
    const validator = new Validator(metadata, { value: metadata.item.extra });
    validator.setAttributeNames({ value: name.toString().toLowerCase() });
    validator.check();
    const errors = validator.errors.get('value');
    if (mutateErrors) {
        // Empty arrays without breaking references
        metadata.errors.splice(0, metadata.errors.length);
        // Add the error objects to the _errors list
        metadata.errors.push(...errors);
    }
    return errors;
}
//# sourceMappingURL=metadata.js.map